/**
 * LocationDetailDrawer — 80% right-side slide-in drawer for center detail.
 *
 * Opens over the Locations list when a center row is clicked. URL reflects
 * the selection (/locations/:id) so deep-links and refresh work correctly.
 * Close via: X button, backdrop click, or Escape key.
 *
 * Mirrors the ClientDetailDrawer shell pattern exactly:
 *   desktop  (>1024px) : min(80vw, 1280px)
 *   landscape tablet (≤1024px) : 90vw
 *   portrait tablet / mobile (≤768px) : 100vw
 *
 * Layout: sticky header (name + status badge + Edit button) → stats strip →
 * button-pill TabBar → tab content body. The TabBar/Tab styled-components
 * carry the exact border + filled-when-selected style from LocationDetailPage.
 */

import { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { X, Pencil } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { media } from '../../styles/media';
import locationService from '../../services/api/locationService';
import { EditLocationModal } from '../LocationManagementPage/EditLocationModal';
import { OverviewTab } from './OverviewTab';
import { RosterTab } from './RosterTab';
import { ChangeHistoryTab } from './ChangeHistoryTab';
import { TransferHistoryTab } from './TransferHistoryTab';
import { CenterScopeTab } from './CenterScopeTab';

// ==================== Types ====================

type ActiveTab = 'overview' | 'roster' | 'history' | 'transfers' | 'scope';

export interface LocationDetailDrawerProps {
  locationId: string;
  onClose: () => void;
  /** When false (default), the Edit Center button is hidden — mirrors ClientDetailDrawer's
   *  canManage prop so the parent owns the permission check. */
  canManage?: boolean;
}

// ==================== Animations ====================

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

// ==================== Styled Components ====================

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: ${({ theme }) => theme.zIndex.modalBackdrop};
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(80vw, 1280px);
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} ${({ theme }) => theme.transitions.base} ease-out;

  ${media.desktop} {
    width: 90vw;
  }

  ${media.tablet} {
    width: 100vw;
  }
`;

/** Sticky header — stays pinned while the body scrolls. */
const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  z-index: 1;
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

const TitleArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
  min-width: 0;
`;

const DrawerTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-shrink: 0;
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  border: none;
  background: none;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

/** Key-facts strip — Clinicians / Clients counts */
const FactsStrip = styled.div`
  display: flex;
  align-items: stretch;
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  overflow-x: auto;

  ${media.tablet} {
    flex-wrap: wrap;
  }
`;

const Fact = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[5]};
  border-right: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  }
`;

const FactLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const FactValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Scrollable content body */
const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`;

// Button/pill-style tabs — border + filled-when-selected, matching LocationDetailPage exactly.
const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
  overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary.base : theme.colors.borders.light)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  white-space: nowrap;
  transition: ${({ theme }) => theme.transitions.fast};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary.base + '12' : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary.base : theme.colors.text.secondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primary.base + '12' : theme.colors.backgrounds.light};
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[16]} ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const NotFoundState = styled.div`
  padding: ${({ theme }) => theme.spacing[16]} ${({ theme }) => theme.spacing[4]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

// ==================== Component ====================

export function LocationDetailDrawer({
  locationId,
  onClose,
  canManage = false,
}: LocationDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [showEdit, setShowEdit] = useState(false);

  const { data: location, isLoading } = useQuery({
    queryKey: ['location', locationId],
    queryFn: () => locationService.getLocationDetail(locationId),
  });

  // Stable ref for Escape — suppressed when the nested Edit modal is open.
  const showEditRef = useRef(showEdit);
  showEditRef.current = showEdit;

  // Close on Escape — read showEditRef so listener is stable for drawer lifetime.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showEditRef.current) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <>
      <Overlay onClick={onClose}>
        <Panel
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`Center detail: ${location?.name ?? 'Center'}`}
        >
          {/* ---- Sticky Header ---- */}
          <StickyHeader>
            <HeaderRow>
              <TitleArea>
                <DrawerTitle>{location?.name ?? (isLoading ? '…' : 'Center')}</DrawerTitle>
                {location && (
                  <Badge variant={location.isActive ? 'success' : 'neutral'}>
                    {location.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                )}
              </TitleArea>
              <HeaderActions>
                {canManage && location && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Pencil size={14} />}
                    onClick={() => setShowEdit(true)}
                  >
                    Edit Center
                  </Button>
                )}
                <CloseBtn onClick={onClose} aria-label="Close center detail">
                  <X />
                </CloseBtn>
              </HeaderActions>
            </HeaderRow>

            {location && (
              <FactsStrip>
                <Fact>
                  <FactLabel>City</FactLabel>
                  <FactValue>
                    {location.city}, {location.state}
                  </FactValue>
                </Fact>
                <Fact>
                  <FactLabel>Clinicians</FactLabel>
                  <FactValue>{location.clinicianCount ?? 0}</FactValue>
                </Fact>
                <Fact>
                  <FactLabel>Clients</FactLabel>
                  <FactValue>{location.clientCount ?? 0}</FactValue>
                </Fact>
              </FactsStrip>
            )}
          </StickyHeader>

          {/* ---- Body ---- */}
          <Body>
            {isLoading ? (
              <LoadingState>
                <LoadingSpinner size="md" />
                Loading center…
              </LoadingState>
            ) : location ? (
              <>
                <TabBar>
                  <Tab $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                    Overview
                  </Tab>
                  <Tab $active={activeTab === 'roster'} onClick={() => setActiveTab('roster')}>
                    Roster
                  </Tab>
                  <Tab $active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
                    Change History
                  </Tab>
                  <Tab
                    $active={activeTab === 'transfers'}
                    onClick={() => setActiveTab('transfers')}
                  >
                    Transfer History
                  </Tab>
                  <Tab $active={activeTab === 'scope'} onClick={() => setActiveTab('scope')}>
                    Center Scope
                  </Tab>
                </TabBar>

                {activeTab === 'overview' && (
                  <OverviewTab location={location} onEditClick={() => setShowEdit(true)} />
                )}
                {activeTab === 'roster' && <RosterTab locationId={locationId} />}
                {activeTab === 'history' && <ChangeHistoryTab locationId={locationId} />}
                {activeTab === 'transfers' && <TransferHistoryTab locationId={locationId} />}
                {activeTab === 'scope' && <CenterScopeTab locationId={locationId} />}
              </>
            ) : (
              <NotFoundState>Center not found.</NotFoundState>
            )}
          </Body>
        </Panel>
      </Overlay>

      {/* EditLocationModal portals into document.body; rendered outside Overlay so
          backdrop clicks don't propagate to the drawer's onClose. */}
      {location && (
        <EditLocationModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          location={location}
        />
      )}
    </>
  );
}

export default LocationDetailDrawer;
